import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Stack,
    Grid,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow
} from '@mui/material';
import {
    AccountWhiteIcon,
    AccountIcon
} from '../../../../assets/icons';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    AdminPanelSettings as AdminIcon,
    Create as CreateIcon,
    Group as GroupIcon,
    Check as CheckIcon,
    Clear as ClearIcon,
    AdminPanelSettings as AdminPanelSettingsIcon
} from '@mui/icons-material';
import { useUserPermissions } from './hooks/useUserPermissions';

export const ConfirmationStep = ({
    selectedUsers,
    availableUsers,
    userPermissions,
    originalFiles,
    currentUserAccess,
    theme
}) => {
    const { getAccessibleFiles, canConfigureFilePermissions } = useUserPermissions(currentUserAccess);
    const accessibleFiles = getAccessibleFiles(originalFiles);

    const getUserById = (userGuid) => {
        return availableUsers.find(user => (user.guid || user.Guid) === userGuid);
    };

    const getRoleConfig = (role) => {
        const config = {
            'Creator': { label: 'Владелец', color: 'primary', icon: <CreateIcon /> },
            'Admin': { label: 'Администратор', color: 'secondary', icon: <AdminIcon /> },
            'Editor': { label: 'Редактор', color: 'success', icon: <EditIcon /> },
            'Viewer': { label: 'Гость', color: 'default', icon: <ViewIcon /> }
        };
        return config[role] || { label: role, color: 'default', icon: theme === 'light' ? <AccountIcon className="account-icon bg-border" /> : <AccountWhiteIcon className="account-icon bg-border" /> };
    };

    const getFilePermissionsForUser = (userPerms, file) => {
        const fileKey = `${file.fileName}_v${file.fileVersion}`;

        // Если файл недоступен - всегда false
        if (!canConfigureFilePermissions(file)) {
            return {
                canRead: false,
                canEdit: false,
                canDeleteOwn: false,
                canDeleteAll: false
            };
        }

        // Если есть кастомные права - используем их
        if (userPerms?.filePermissions && userPerms.filePermissions[fileKey]) {
            const customPerms = userPerms.filePermissions[fileKey];
            return {
                canRead: Boolean(customPerms.canRead),
                canEdit: Boolean(customPerms.canEdit),
                canDeleteOwn: Boolean(customPerms.canDeleteOwnVersions),
                canDeleteAll: Boolean(customPerms.canDeleteAllVersions)
            };
        }

        // Иначе - права по умолчанию для роли
        const defaultPermissions = {
            'Viewer': { canRead: true, canEdit: false, canDeleteOwn: false, canDeleteAll: false },
            'Editor': { canRead: true, canEdit: true, canDeleteOwn: true, canDeleteAll: false },
            'Admin': { canRead: true, canEdit: true, canDeleteOwn: true, canDeleteAll: true },
            'Creator': { canRead: true, canEdit: true, canDeleteOwn: true, canDeleteAll: true }
        };

        return defaultPermissions[userPerms?.role || 'Viewer'] || defaultPermissions['Viewer'];
    };

    const getPermissionIcon = (hasPermission) => {
        return hasPermission ?
            <CheckIcon color="success" fontSize="small" /> :
            <ClearIcon color="error" fontSize="small" />;
    };

    if (selectedUsers.length === 0) {
        return (
            <Box textAlign="center" py={4}>
                <Typography color="textSecondary">
                    Нет выбранных пользователей
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Подтверждение прав доступа
            </Typography>

            {/* Общая информация */}
            <Card className="summary-info">
                <CardContent className="summary-info">
                    <Stack direction="row" spacing={3} alignItems="center" justifyContent="center">
                        <Box sx={{ textAlign: 'center' }}>
                            <GroupIcon sx={{ fontSize: 40 }} />
                            <Typography variant="h6">{selectedUsers.length}</Typography>
                            <Typography variant="caption">{selectedUsers.length === 1 ? "пользователь" : (selectedUsers.length === 2 ? "пользователя" : "пользователей")}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <span className="material-symbols-outlined summary-docs">
                                docs
                            </span>
                            <Typography variant="h6">{accessibleFiles.length}</Typography>
                            <Typography variant="caption">{accessibleFiles.length === 1 ? "файл" : (accessibleFiles.length === 2 ? "файла" : "файлов")}</Typography>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {/* Детализированная информация по пользователям и файлам */}
            <Stack spacing={3}>
                {selectedUsers.map(userGuid => {
                    const user = getUserById(userGuid);
                    const userPerms = userPermissions[userGuid];
                    const roleConfig = getRoleConfig(userPerms?.role);

                    return (
                        <Card key={userGuid} variant="outlined">
                            <CardContent>
                                {/* Заголовок пользователя */}
                                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                    <Grid item>
                                        {theme === 'light' ? <AccountIcon className="account-icon bg-border" /> : <AccountWhiteIcon className="account-icon bg-border" />}
                                    </Grid>
                                    <Grid item xs>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="subtitle1" fontWeight="medium">
                                                {user?.fullName || user?.login}
                                            </Typography>
                                            {userPerms?.customCanManageTeam && (
                                                <Tooltip title="Может управлять командой">
                                                    <AdminPanelSettingsIcon
                                                        fontSize="small"
                                                        color="action"
                                                        className="ml-1 team-manager"
                                                    />
                                                </Tooltip>
                                            )}
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="subtitle2" fontWeight="medium">
                                                {user?.login}
                                            </Typography>
                                            <Chip
                                                className="chip-role"
                                                label={roleConfig.label}
                                                color={roleConfig.color}
                                                size="small"
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>

                                {/* Таблица прав доступа к файлам */}
                                {accessibleFiles.length > 0 && (
                                    <Box className="mt-4">
                                        <Typography variant="subtitle2" gutterBottom>
                                            Права доступа к файлам:
                                        </Typography>
                                        <Table size="small" className="mt-3">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell className="standard-text">Файл</TableCell>
                                                    <TableCell className="standard-text" align="center">
                                                        <ViewIcon fontSize="medium" />
                                                    </TableCell>
                                                    <TableCell className="standard-text" align="center">
                                                        <EditIcon fontSize="medium" />
                                                    </TableCell>
                                                    <TableCell className="standard-text" align="center">
                                                        <Box>
                                                            <DeleteIcon fontSize="small" />
                                                            <Typography variant="caption" display="block">Свои</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell className="standard-text" align="center">
                                                        <Box>
                                                            <DeleteIcon fontSize="small" />
                                                            <Typography variant="caption" display="block">Все</Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {accessibleFiles.map((file, index) => {
                                                    const filePerms = getFilePermissionsForUser(userPerms, file);

                                                    return (
                                                        <TableRow key={index}>
                                                            <TableCell>
                                                                <Typography variant="body2">
                                                                    {file.fileName}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {getPermissionIcon(filePerms.canRead)}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {getPermissionIcon(filePerms.canEdit)}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {getPermissionIcon(filePerms.canDeleteOwn)}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {getPermissionIcon(filePerms.canDeleteAll)}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
            </Stack>
        </Box>
    );
};